import * as React from "react";

import { Label, LabelGroup } from "Stores/labelStore";
import { Utils } from "../../common/utils";

import {Alert, Button, Col, Form, Row, Spinner} from "react-bootstrap";
import { CrudModal } from "Components/Common/CrudModal";
import { SelectWrapper } from "Components/Common/SelectWrapper";
import { ColumnDef } from "@tanstack/react-table";
import { ActionWrapper } from "Components/Common/ActionWrapper";
import { EnfluxCheckbox } from "Components/Common/EnfluxCheckbox";
import { isLabelActiveFilter } from "../../common/filters";
import { StyledTable } from "Components/Common/StyledTable";
import { PlusIcon } from "Components/Icons/PlusIcon";
import DebouncedInput from "Components/Common/DebouncedInput";
import { MagnifyingIcon } from "Components/Icons/MagnifyingIcon";
import {useState} from "react";
import { Defaults } from "@/common/utils";
import {createLabel, deleteLabel, updateLabel, useLabelGroups, useLabels} from "@/hooks/labels";
import {useAppContext} from "@/contexts/app";
import PageTitle from "Components/Common/PageTitle";
import { useToast } from "@/hooks/useToast";


export function LabelManagementPage() {
    const [showModal, setShowModal] = useState(false);
    const [currentLabel, setCurrentLabel] = useState<Label>();
    const [formName, setFormName] = useState("");
    const [formGroup, setFormGroup] = useState<LabelGroup | null>(null);
    const [searchValue, setSearchValue] = useState("");
    const [pageSize, setPageSize] = useState(Defaults.tablePageSize());
    const {data: dataLabels, mutate: mutateLabels} = useLabels();
    const {data: dataLabelGroups, mutate: mutateLabelGroups} = useLabelGroups();
    const {dispatchError} = useAppContext();

    const { toast } = useToast();

    const columns: ColumnDef<Label>[] = [
        {
            header: "Name",
            accessorKey: "name",
            size: 200,
        },
        {
            header: "Group",
            id: "group_name",
            accessorFn: (label: Label) => label.label_group?.name ?? "",
            cell: cellProps => (<div>{cellProps.row.original.label_group?.name || ""}</div>),
            minSize: 70,
            maxSize: 200
        },
        {
            header: "Active",
            accessorKey: "is_archived",
            cell: ({row}) => (
                <div className="action-button-group">
                    {renderCheckBox(row.original)}
                    {renderActionButton(row.original)}
                </div>
            ),
            filterFn: isLabelActiveFilter
        },
    ];

    const handleNameChange = (ev: any) => {
        setFormName(ev.target.value);
    };

    const handleLabelGroupChange = (option) => {
        setFormGroup(option);
    };

    const closeModal = () => {
        setShowModal(false);
        setCurrentLabel(undefined);
    };

    const handleCreate = () => {
        setShowModal(true);
        setCurrentLabel(undefined);
        setFormName("");
        setFormGroup(null);
    };

    const handleEdit = (label: Label) => {
        setShowModal(true);
        setCurrentLabel(label);
        setFormName(label.name);
        setFormGroup(label.label_group ?? null);
    };

    const handleDelete = async (label: Label) => {
        try {
            await deleteLabel(label.id);
            await mutateLabels();
            await mutateLabelGroups();
        } catch (e) {
            dispatchError(e);
        }
    };

    const handleSave = async () => {
        try {
            const data = { name: formName, label_group: formGroup?.id };
            if (currentLabel) {
                await updateLabel(currentLabel, data);
            } else {
                await createLabel(data);
            }

            toast({
                type: "success",
                title: "Label Group Updated",
                message: `Updated Label Group to ${formGroup?.name}`,
            });

            await Promise.all([
                mutateLabels(),
                mutateLabelGroups(),
            ]);

        } catch (e) {
            dispatchError(e);
        } finally { 
            closeModal();
        }
    };

    const toggleArchivedLabel = async (label: Label) => {
        const data = {name: label.name, label_group: label.label_group, is_archived: !label.is_archived};
        try {
            await updateLabel(label, data);
            await mutateLabels();
        } catch (e) {
            dispatchError(e);
        }
    };

    const getLabelGroupLabel = (group: LabelGroup) => group.name;
    const getLabelGroupValue = (group) => group.id;

    const renderActionButton = (obj: Label) => {
        const actionProps = {
            data: obj,
            // Only show edit button if onEdit is defined and, if getCanEdit is defined, getCanEdit returns true
            onEdit: handleEdit,
            // Only show delete button if onDelete is defined and, if getCanDelete is defined, getCanDelete returns true
            onDelete: handleDelete,
        };
        return (<ActionWrapper { ...actionProps } />);
    };

    const renderCheckBox = (label: Label) => {
        return (
            <div className="active-checkbox">
                <EnfluxCheckbox
                    id={`isArchived_${label.id}`}
                    isChecked={!label.is_archived}
                    onChange={() => toggleArchivedLabel(label)}
                />
            </div>
        );
    };

    const renderModalForm = () => {
        return (
            <React.Fragment>
                <Row>
                    <Col>
                        <label>Name</label>
                        <Form.Control id="label-modal-form-name-input" onChange={ handleNameChange } value={ formName }/>
                    </Col>
                </Row>
                <Row>
                    <Col id="label-modal-form-group-col">
                        <label>Group</label>
                        <SelectWrapper
                            id="label-modal-form-group-select"
                            options={ dataLabelGroups?.data || [] }
                            value={ formGroup }
                            getOptionLabel={ getLabelGroupLabel }
                            getOptionValue={ getLabelGroupValue }
                            placeholder="Select a label group"
                            onChange={ handleLabelGroupChange }
                        />
                    </Col>
                </Row>
            </React.Fragment>
        );
    };
    return (
        <div className="card-layout">
            <PageTitle pageTitle="Labels" />
            {Utils.hasAdminAccess()
                ? <React.Fragment>
                    <Row>
                        <Col md={12} lg={6} className="title">
                            <h3>Labels</h3>
                        </Col>
                        <Col>
                            <div className="create-button">
                                <Button
                                    id="label-create-button"
                                    data-test-id="labels-create-btn"
                                    variant="primary"
                                    className="d-flex align-items-center new-label-btn gap-2 text-nowrap"
                                    onClick={ handleCreate }
                                >
                                    <PlusIcon className="white d-flex" />
                                    create label
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    <div className="search-bar">
                        <DebouncedInput
                            type="text"
                            value={searchValue}
                            onChange={(value) => setSearchValue(value as string)}
                            placeholder="Search by Name or Group"
                            className="search-input"
                            data-test-id="labels-search-input"
                        />
                        <MagnifyingIcon />
                    </div>

                    {!dataLabels
                        ?<Spinner animation="border" variant={"secondary"}/>
                        :<div className="list labels">
                            <LabelsListReactTable columns={columns} data={dataLabels?.data ?? []} searchValue={searchValue} pageSize={pageSize} setPageSize={setPageSize}/>

                            { showModal
                                ? <CrudModal
                                    typeLabel={ "Label" }
                                    isEdit={ !!currentLabel }
                                    renderForm={ renderModalForm }
                                    onClose={ closeModal }
                                    onSave={ handleSave }
                                    isDisabled={ !formName || !formGroup }
                                    showSpinner={ !dataLabels }/>
                                : null }
                        </div>
                    }
                </React.Fragment>
                : <div className="col-sm-12col col-md-12">
                    <Alert key="Label-Warning" variant="warning">
                        {`Sorry, you don't have access to label management. If you think this is an error,\n
                            please contact your administrator so he/she can give you the correct access level.`}
                    </Alert>
                </div>
            }
        </div>
    );
}

interface LabelsListReactTableProps {
    readonly columns: ColumnDef<Label>[]
    readonly data: Label[]
    readonly searchValue?: string
    readonly pageSize: number
    readonly setPageSize: (n: number) => void
}

function LabelsListReactTable({columns, data, searchValue, pageSize, setPageSize}: LabelsListReactTableProps) {

    const filteredData = React.useMemo(() => {
        if (!searchValue) return data;
        const lowerSearchValue = searchValue.toLowerCase();
        return data.filter((label) => {
            const nameMatches = label.name.toLowerCase().includes(lowerSearchValue);
            const dashboardMatches = label.label_group?.name.toLowerCase().includes(lowerSearchValue);
            return nameMatches || dashboardMatches;
        });
    }, [data, searchValue]);

    return (
        <StyledTable
            defaultData={filteredData}
            columns={columns}
            className="labels-table"
            pageSize={pageSize}
            setPageSize={setPageSize}
        />
    );
}
